import { createContext, useState, useContext, useEffect } from "react";
import { jwtDecode } from "jwt-decode";

// Create a Context for authentication
const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  let [userData, setUserData] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // **Login function**
  const login = (token, info) => {
    setToken(token);
    setUserData(info);
    localStorage.setItem("token", token);
    localStorage.setItem("userData", JSON.stringify(info));
    setLoading(false);
  };

  // **Logout function**
  const logout = () => {
    setUserData(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    setLoading(false);
  };

  // **NEW: Function to update userData**
  const updateUserData = (updatedInfo) => {
    setUserData(updatedInfo);
    localStorage.setItem("userData", JSON.stringify(updatedInfo));
  };

  // **Check for existing token & user data on app load**
  useEffect(() => {
    const existingToken = localStorage.getItem("token");
    const existingUserData = localStorage.getItem("userData");

    if (existingToken && existingUserData) {
      setToken(existingToken);
      setUserData(JSON.parse(existingUserData));
    }

    setLoading(false);
  }, []);

  // **Auto logout when token expires**
  useEffect(() => {
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      const exp = decoded.exp; // Expiration timestamp (seconds)
      const now = Date.now() / 1000; // Current timestamp (seconds)

      if (exp < now) {
        logout(); // Token expired, logout
      } else {
        // Auto logout when token expires
        const timeUntilExp = (exp - now) * 1000;
        const timer = setTimeout(logout, timeUntilExp);

        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      logout();
    }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        userData,
        setUserData,
        updateUserData,
        token,
        login,
        logout,
        loading,
        setLoading,
      }}
    >
      {loading ? <div>Loading...</div> : children}
    </AuthContext.Provider>
  );
};
